import { api, getApiKeyFromLocalStorage, getBaseURL } from './apiClient';

export const ingestFile = async (file, domain = 'main') => {
  try {
    const formData = new FormData();
    
    formData.append('files', file);
    
    if (domain) {
      formData.append('domain', domain);
    }
    
    const response = await api.post('/file/ingest', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Ingest file error:', error);
    if (error.response?.status === 401) {
      throw new Error('Unauthorized: Please check your API key');
    }
    throw new Error(error.response?.data?.error || 'Failed to ingest file');
  }
};

export const getFiles = async (domain = 'main', page = 1, limit = 30) => {
  try {
    const response = await fetch(`${getBaseURL()}/file?domain=${domain}&limit=${limit}&page=${page}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getApiKeyFromLocalStorage()
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized: Please check your API key');
      }
      throw new Error('Failed to fetch files');
    }

    return await response.json();
  } catch (error) {
    console.error('Get files error:', error);
    throw error;
  }
};

export const getFile = async (fileId) => {
    const apiKey = getApiKeyFromLocalStorage();
    if (!apiKey) {
      throw new Error('API key is missing');
    }

    const response = await fetch(`${getBaseURL()}/file/${fileId}`, {
        method: 'GET',
        headers: {
            'x-api-key': apiKey,
        }
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
};

export const getFileByName = async (filename) => {
    const apiKey = getApiKeyFromLocalStorage();
    if (!apiKey) {
      throw new Error('API key is missing');
    }

    const url = `${getBaseURL()}/file/name/${filename}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'x-api-key': apiKey,
        }
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.blob();
};

export const deleteFile = async (fileId, domain = 'main') => {
  try {
    const response = await api.delete(`/file/${fileId}`, {
      data: { domain }
    });
    
    return response.data;
  } catch (error) {
    console.error('Delete file error:', error);
    if (error.response?.status === 401) {
      throw new Error('Unauthorized: Please check your API key');
    }
    throw new Error(error.response?.data?.error || 'Failed to delete file');
  }
};

export const crawlUrl = async (url, domain = 'main', maxPage = 100, maxDepth = 3, crawlSubDomain = true, fetchFiles = false) => {
  try {
    const response = await api.post('/file/crawl', {
      url,
      domain,
      max_page: maxPage,
      max_depth: maxDepth,
      crawl_sub_domain: crawlSubDomain,
      fetch_files: fetchFiles
    });

    return response.data;
  } catch (error) {
    console.error('Crawl URL error:', error);
    if (error.response?.status === 401) {
      throw new Error('Unauthorized: Please check your API key');
    }
    throw new Error(error.response?.data?.error || 'Failed to crawl URL');
  }
};

export const getUploadStatus = async (uploadId) => {
  if (!uploadId) {
    throw new Error('Upload ID is required for checking status');
  }
  
  const apiKey = getApiKeyFromLocalStorage();
  if (!apiKey) {
    throw new Error('API key is missing');
  }

  const response = await fetch(`${getBaseURL()}/file/upload/${uploadId}`, {
    method: 'GET',
    headers: {
      'x-api-key': apiKey,
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

export const generateQuery = async ({ query, model, domain = 'main' }) => {
  try {
    const response = await api.post('/query', {
      query,
      model,
      domain
    });
    
    return response.data;
  } catch (error) {
    console.error('Generate query error:', error);
    if (error.response?.status === 401) {
      throw new Error('Unauthorized: Please check your API key');
    }
    throw new Error(error.response?.data?.error || 'Failed to generate query');
  }
};

export const deleteFiles = async (fileIds, domain = 'main') => {
  if (!fileIds || !Array.isArray(fileIds) || fileIds.length === 0) {
    throw new Error('File IDs array is required');
  }

  const results = await Promise.allSettled(
    fileIds.map(fileId => deleteFile(fileId, domain))
  );

  const failed = results.filter(r => r.status === 'rejected');
  if (failed.length > 0) {
    console.error('Some file deletions failed:', failed);
  }

  return {
    success: results.filter(r => r.status === 'fulfilled').length,
    failed: failed.length,
    total: fileIds.length
  };
};

export const uploadTempFiles = async (files, sessionId) => {
  if (!sessionId) {
    throw new Error('Session ID is required for temp file upload');
  }

  try {
    const formData = new FormData();
    
    files.forEach(file => {
      formData.append('files', file);
    });
    
    formData.append('session', sessionId);
    
    const response = await api.post('/file/temp', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Upload temp files error:', error);
    if (error.response?.status === 401) {
      throw new Error('Unauthorized: Please check your API key');
    }
    throw new Error(error.response?.data?.error || 'Failed to upload temporary files');
  }
};


